import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FileSpreadsheet, FileText, Upload } from 'lucide-react';
import DataTable from '../components/DataTable';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

// Resumen diario de MIA: quien marco ese dia, su entrada, su salida y el total menos los 30 min de
// comida. Solo lectura y directo de los punches — no recalcula ni depende de que la semana ya este
// calculada (ver backend/routes/dailyHours.js).

// El modo "Por fecha" (punches ya guardados en la base) esta escondido hasta que lo confirmen: por
// ahora solo se usa desde archivo. Para liberarlo basta con poner esto en true — el backend ya lo
// sirve.
const SHOW_DATE_MODE = false;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtHrs = (val) => {
  if (val === null || val === undefined) return '-';
  const totalSec = Math.round(val * 3600);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const fmtTime12 = (t) => {
  if (!t) return null;
  const [hh, mm, ss] = String(t).split(':');
  let h = Number(hh);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm}:${ss || '00'} ${ampm}`;
};

export default function DailyHours() {
  const { t } = useLanguage();
  // 'date' = los punches ya guardados en la base; 'file' = un reporte de accesos subido aqui, que se
  // procesa sin importarlo.
  const [mode, setMode] = useState(SHOW_DATE_MODE ? 'date' : 'file');
  const [date, setDate] = useState(SHOW_DATE_MODE ? todayISO() : '');
  const [data, setData] = useState({ rows: [], grandTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filePunches, setFilePunches] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileDates, setFileDates] = useState([]);
  // Archivo con varios dias: true = un solo reporte con todos, false = un dia a la vez.
  const [combined, setCombined] = useState(false);
  const fileRef = useRef();

  const fetchData = useCallback(async () => {
    if (mode === 'file') {
      if (!filePunches) { setData({ rows: [], grandTotal: 0 }); setLoading(false); return; }
      setLoading(true);
      try {
        const res = await axios.post('/api/daily-hours/from-file', { date, combined, punches: filePunches });
        setFileDates(res.data.fileDates || []);
        if (!res.data.combined && res.data.date !== date) setDate(res.data.date);
        setData(res.data);
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('daily.fetchError') });
      }
      setLoading(false);
      return;
    }
    if (!date) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/daily-hours', { params: { date } });
      setData(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('daily.fetchError') });
    }
    setLoading(false);
  }, [mode, date, combined, filePunches, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mismo parseo que la importacion de Reportes: la fecha y hora se leen en hora LOCAL, para que un
  // punch de la noche no se corra al dia siguiente.
  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await file.arrayBuffer());
      const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
      const punches = [];
      for (const row of json) {
        const dt = new Date(row['Date/Time'] || '');
        if (isNaN(dt)) continue;
        const pad = (n) => String(n).padStart(2, '0');
        punches.push({
          badge: row['User No.'],
          name: row['User Name'] || '',
          date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
          time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`,
          door: row['Door Name'] || '',
        });
      }
      if (punches.length === 0) {
        Swal.fire({ icon: 'warning', title: t('common.error'), text: t('daily.fileEmpty') });
        return;
      }
      // Varios dias en el archivo: se pregunta si van en un solo reporte o separados.
      const days = [...new Set(punches.map(p => p.date))].sort();
      let joinDays = false;
      if (days.length > 1) {
        const fmt = (s) => { const [y, m, d] = s.split('-'); return `${m}/${d}/${y}`; };
        const answer = await Swal.fire({
          icon: 'question',
          title: t('daily.multiDayTitle'),
          html: `${t('daily.multiDayMsg')}<br><b>${days.map(fmt).join(', ')}</b>`,
          showDenyButton: true,
          confirmButtonText: t('daily.combineYes'),
          denyButtonText: t('daily.combineNo'),
          denyButtonColor: '#64748b',
          allowOutsideClick: false,
        });
        joinDays = answer.isConfirmed;
      }
      setFileName(file.name);
      setCombined(joinDays);
      // Fecha vacia: el backend elige el dia del archivo con mas punches.
      setDate('');
      setFilePunches(punches);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('common.fileReadError') });
    }
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setData({ rows: [], grandTotal: 0 });
    if (next === 'date') {
      setFilePunches(null);
      setFileName('');
      setFileDates([]);
      setCombined(false);
      setDate(todayISO());
    }
  };

  // format = 'pdf' | 'excel'. `exporting` guarda cual se esta bajando, para el texto del boton.
  const download = async (format) => {
    setExporting(format);
    try {
      const res = mode === 'file'
        ? await axios.post(`/api/daily-hours/from-file/export/${format}`,
          { date, combined, punches: filePunches, fileName }, { responseType: 'blob' })
        : await axios.get(`/api/daily-hours/export/${format}`, { params: { date }, responseType: 'blob' });
      const isPdf = format === 'pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      // Mismo nombre que arma el backend: MMDDYYYY, o el rango si el reporte junta varios dias.
      const compact = (s) => { const [y, m, d] = s.split('-'); return `${m}${d}${y}`; };
      const stamp = data.combined
        ? `${compact(data.date)}-${compact(data.dateTo)}`
        : compact(data.date || date);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DAILY HOURS MIA ${stamp}.${isPdf ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('daily.exportError') });
    }
    setExporting(false);
  };

  const missing = (label) => <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{label}</span>;

  const fmtRowDate = (s) => { const [y, m, d] = s.split('-'); return `${m}/${d}/${y}`; };

  const columns = [
    ...(data.combined ? [{ header: t('daily.date'), render: (r) => fmtRowDate(r.date) }] : []),
    { header: t('common.badge'), accessor: 'badge' },
    {
      header: t('hrs.employee'),
      render: (r) => r.registered ? r.name : (
        <span>{r.name ? `${r.name} ` : ''}{missing(`(${t('daily.notRegistered')})`)}</span>
      ),
    },
    { header: t('daily.in'), render: (r) => fmtTime12(r.clockIn) || missing('—') },
    { header: t('daily.out'), render: (r) => fmtTime12(r.clockOut) || missing('—') },
    { header: t('daily.gross'), render: (r) => r.incomplete ? '-' : fmtHrs(r.gross) },
    {
      header: t('daily.total'),
      render: (r) => r.incomplete
        ? <span title={t('daily.incompleteTip')}>{missing(r.clockIn ? t('daily.noOut') : t('daily.noIn'))}</span>
        : <strong style={{ color: 'var(--accent)' }}>{fmtHrs(r.total)}</strong>,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('daily.title')}</h1>
      </div>

      <div className="filters-bar">
        {SHOW_DATE_MODE && (
          <select className="form-input" style={{ width: 190, padding: '8px 10px', height: 38 }}
            value={mode} onChange={e => switchMode(e.target.value)}>
            <option value="date">{t('daily.modeDate')}</option>
            <option value="file">{t('daily.modeFile')}</option>
          </select>
        )}
        {mode === 'date' ? (
          <div style={{ width: 170 }}>
            <DateInput className="form-input" value={date} onChange={setDate} />
          </div>
        ) : (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
            <button className="btn-primary" style={{ width: 'auto', padding: '8px 18px', background: 'var(--accent)' }}
              onClick={() => fileRef.current?.click()}>
              <Upload size={15} style={{ marginRight: 6 }} />
              {filePunches ? t('daily.changeFile') : t('daily.loadFile')}
            </button>
            {fileName && (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }} title={fileName}>{fileName}</span>
            )}
            {/* Un archivo puede traer mas de un dia (el guardia de la noche anterior): se elige si
                van juntos en un solo reporte o un dia a la vez, y en ese caso cual. */}
            {fileDates.length > 1 && (
              <select className="form-input" style={{ width: 190, padding: '8px 10px', height: 38 }}
                value={combined ? 'one' : 'split'} onChange={e => setCombined(e.target.value === 'one')}>
                <option value="one">{t('daily.combineYes')}</option>
                <option value="split">{t('daily.combineNo')}</option>
              </select>
            )}
            {fileDates.length > 1 && !combined && (
              <select className="form-input" style={{ width: 200, padding: '8px 10px', height: 38 }}
                value={date} onChange={e => setDate(e.target.value)}>
                {fileDates.map(fd => {
                  const [y, m, d] = fd.date.split('-');
                  return <option key={fd.date} value={fd.date}>{`${m}/${d}/${y} (${fd.count} punches)`}</option>;
                })}
              </select>
            )}
            {fileDates.length === 1 && (
              <span className="badge badge-info">{(() => { const [y, m, d] = fileDates[0].date.split('-'); return `${m}/${d}/${y}`; })()}</span>
            )}
          </>
        )}
        <span className="badge badge-info">MIA</span>
        <button className="btn-primary" style={{ width: 'auto', padding: '8px 18px', background: 'var(--danger)' }}
          onClick={() => download('pdf')} disabled={!!exporting || loading || data.rows.length === 0}>
          <FileText size={15} style={{ marginRight: 6 }} />
          {exporting === 'pdf' ? t('common.exporting') : t('daily.downloadPdf')}
        </button>
        <button className="btn-primary" style={{ width: 'auto', padding: '8px 18px', background: 'var(--success)' }}
          onClick={() => download('excel')} disabled={!!exporting || loading || data.rows.length === 0}>
          <FileSpreadsheet size={15} style={{ marginRight: 6 }} />
          {exporting === 'excel' ? t('common.exporting') : t('daily.downloadExcel')}
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px 2px' }}>
        {t('daily.legend')}
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={data.rows} loading={loading}
          emptyMessage={mode === 'file' && !filePunches ? t('daily.pickFile') : t('daily.empty')} className="table-compact" />
        {!loading && data.rows.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '10px 14px', fontWeight: 700 }}>
            {/* Con varios dias una persona puede salir en mas de una fila: se cuenta por badge. */}
            <span>{new Set(data.rows.map(r => r.badge)).size} {t('daily.employees')}</span>
            <span>{t('daily.gross')}: {fmtHrs(data.grandGross)}</span>
            <span>{t('daily.grandTotal')}: <span style={{ color: 'var(--accent)' }}>{fmtHrs(data.grandTotal)}</span></span>
          </div>
        )}
      </div>
    </div>
  );
}
